import { bindActionCreators } from '@reduxjs/toolkit';
import React, { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { all } from '../../actions/branding';
import { track } from '../../actions/tracking';
import OverflowSwipe from '../../components/overflow-swipe';
import type { RootState } from '../../reducers';
import {
  getManufacturePackagingForCurrentItem,
  getMrfPackagingForCurrentItem,
  shouldFetchBrandings as shouldFetchBrandingsSelector,
} from '../../selectors/branding';
import { getItem } from '../../selectors/item';
import { getRetailerAbout } from '../../selectors/retailer';
import {
  type ResellerPackagingAsPromo,
  getItemResellerPackagingsAsPromo,
} from '../../selectors/search-retailer';
import { GuaranteeEvents, conversionType } from '../../shared/tracking';
import { H3 } from '../../styled-components/components/Heading';
import GuaranteePackageItem from './guarantee-package-item';

const GuaranteePackage = () => {
  const { t } = useTranslation(['common', 'item']);
  const dispatch = useDispatch();
  const item = useSelector(getItem);
  const shouldFetchBrandings = useSelector(shouldFetchBrandingsSelector);
  const manufacturerPackaging = useSelector(getManufacturePackagingForCurrentItem);
  const resellerPackagings = useSelector<RootState, ResellerPackagingAsPromo[]>((s) =>
    getItemResellerPackagingsAsPromo(s, item),
  );
  const mrfPackagings = useSelector(getMrfPackagingForCurrentItem);
  const retailerAbout = useSelector(getRetailerAbout);
  const actions = bindActionCreators(
    {
      getBrandings: all,
      track,
    },
    dispatch,
  );

  useEffect(() => {
    if (shouldFetchBrandings) {
      actions.getBrandings();
    }
  }, [shouldFetchBrandings]);

  const onTrackClick = useCallback((cardType: string, name: string, position: number) => {
    actions.track(GuaranteeEvents.GUARANTEE_INTERACTION, {
      conversionType: conversionType.LINK_CLICK,
      spreadable: {
        interaction: {
          cardType,
          name,
          position,
        },
      },
    });
  }, []);

  const onTrackOutboundLinkClick = useCallback(
    (cardType: string, name: string, position: number) => {
      actions.track(GuaranteeEvents.GUARANTEE_INTERACTION, {
        conversionType: conversionType.OUTBOUND_CLICK,
        spreadable: {
          interaction: {
            cardType,
            name,
            position,
          },
        },
      });
    },
    [],
  );

  if (!manufacturerPackaging && resellerPackagings.length === 0 && !mrfPackagings.length)
    return null;

  const overflowList = [];

  if (manufacturerPackaging) {
    overflowList.push(
      <GuaranteePackageItem
        index={0}
        title={manufacturerPackaging.name}
        logo={manufacturerPackaging.logo || undefined}
        onTrackClick={onTrackClick}
        onTrackOutboundLinkClick={onTrackOutboundLinkClick}
        description={manufacturerPackaging.description}
        linkUrl={manufacturerPackaging.website}
        linkName={t('moreInformation', { ns: 'common' })}
        dangerousHtml={true}
        key={manufacturerPackaging.name}
        cardType="tillverkarpaketering"
      />,
    );
  }

  resellerPackagings?.forEach((resellerPackage, index) => {
    overflowList.push(
      <GuaranteePackageItem
        index={(manufacturerPackaging ? 1 : 0) + index}
        title={resellerPackage.title}
        image={resellerPackage.image}
        onTrackClick={onTrackClick}
        onTrackOutboundLinkClick={onTrackOutboundLinkClick}
        logo={retailerAbout?.logo}
        description={resellerPackage.description}
        linkUrl={resellerPackage.link.url}
        linkName={resellerPackage.link.name}
        dangerousHtml={true}
        key={resellerPackage.title}
        cardType="handlarpaketering"
      />,
    );
  });

  mrfPackagings?.forEach((mrfPackage, index) => {
    overflowList.push(
      <GuaranteePackageItem
        index={(manufacturerPackaging ? 1 : 0) + resellerPackagings.length + index}
        title={mrfPackage.name}
        logo={mrfPackage.logo?.file.url}
        onTrackClick={onTrackClick}
        onTrackOutboundLinkClick={onTrackOutboundLinkClick}
        description={mrfPackage.description}
        linkUrl={mrfPackage.website}
        linkName={t('moreInformation', { ns: 'common' })}
        dangerousHtml={true}
        key={mrfPackage.name}
        cardType="mrf-garanti"
      />,
    );
  });

  return (
    <div className="item-section">
      <H3 as="h2" $margin="small">
        {t('guaranteePackage.heading', { ns: 'item' })}
      </H3>
      <div className="m-t">
        <div data-am-content="">
          <p>{t('guaranteePackage.description', { ns: 'item' })}</p>
        </div>
      </div>
      <div className="m-t-double">
        <OverflowSwipe visibleItems={2} visibleItemsMobile={1}>
          {overflowList}
        </OverflowSwipe>
      </div>
    </div>
  );
};

export default GuaranteePackage;
